/**
 * Compact voice panel (T6, docs/ARCHITECTURE.md §9, §12). Mounted twice by `SessionPage.tsx` from a single
 * `useVoice()` call — once inside `Lobby`'s children slot (full size) and once inside `PlaySurface`'s header
 * (`compact`) — so it always reflects the one voice connection that persists across the whole session.
 *
 * Every indicator here is text first: "speaking" and "muted" are two separate labelled badges, never colour
 * alone, and never the same element as turn ownership (the module renderers own that). Reduced motion drops
 * the speaking pulse in favour of a static badge.
 */
import { DecisionMakerView, DECISION_MAKER_ID, HOT_POTATO_ID, HotPotatoView, MOMENTUM_WORLD_MODULE_IDS } from '@momentum/modules/manifests';
import { useSessionContext } from '../session/SessionContext.ts';
import { CharacterBadge } from '../components/CharacterBadge.tsx';
import { strings } from '../shell/strings.ts';
import { BerryAvatar } from '../shell/BerryAvatar.tsx';
import { voiceUiState } from './uiState.ts';
import type { UseVoiceResult } from './useVoice.ts';
import { BoardView, type CharacterSlotId, type ParticipantPublic } from '@momentum/shared';
import './voice-strip.css';
import { useDecisionNoteBridge } from '../session/DecisionNoteBridge.tsx';

export interface VoicePanelProps {
  voice: UseVoiceResult;
  /** Smaller footprint for `PlaySurface`'s header strip; the lobby renders the full panel. */
  compact?: boolean;
}

/** Scoped, static: only referenced by `.voice-speaking-pulse` below, and a no-op under reduced motion. */
const PULSE_STYLE = `
@keyframes momentum-voice-speaking-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 45%, transparent); }
  50% { box-shadow: 0 0 0 5px color-mix(in srgb, var(--accent) 0%, transparent); }
}
.voice-speaking-pulse { animation: momentum-voice-speaking-pulse 1.2s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .voice-speaking-pulse { animation: none; }
}
`;

export function VoicePanel({ voice, compact }: VoicePanelProps) {
  const { view, reducedMotion } = useSessionContext();
  const session = view.session;
  const presenterSession = session.settings.presenter;
  const ui = voiceUiState({
    mode: voice.mode,
    status: voice.status,
    detail: voice.detail,
    muted: voice.muted,
    everUnmuted: voice.everUnmuted,
    canPublish: voice.canPublish,
  });

  const modeLabel =
    voice.mode === 'remote' ? strings.voice.modeRemote : voice.mode === 'in_person' ? strings.voice.modeInPerson : strings.voice.modeOff;
  const modeHint =
    voice.mode === 'remote' ? strings.voice.modeRemoteHint : voice.mode === 'in_person' ? strings.voice.modeInPersonHint : strings.voice.modeOffHint;
  const providerNote =
    !ui.isOff && voice.provider === 'mock'
      ? presenterSession
        ? strings.voice.simulatedPresenterMode
        : strings.voice.simulatedNoProvider
      : null;
  const stripTitle = presenterSession && voice.provider === 'mock' ? strings.voice.simulatedActivityStrip : strings.voice.participantsTitle;

  if (compact) return <CompactVoiceStrip voice={voice} />;

  return (
    <section className="card stack-sm" aria-label={strings.voice.panelTitle}>
      <style>{PULSE_STYLE}</style>
      <div className="row-between">
        {compact ? null : <h2>{strings.voice.panelTitle}</h2>}
        <span className="badge">{modeLabel}</span>
      </div>

      {ui.isOff ? (
        <p className="text-muted">{modeHint}</p>
      ) : (
        <>
          {providerNote ? <p className="badge badge-highlight">{providerNote}</p> : null}
          {!compact ? <p className="text-muted">{modeHint}</p> : null}

          <p role="status" aria-live="polite" className={voice.status === 'connected' ? 'badge badge-success' : 'badge'}>
            {voice.status === 'connected' && voice.muted ? strings.voice.statusListening : ui.statusLabel}
          </p>
          {ui.showEnableMicButton ? <p className="text-muted">{strings.voice.joinMicrophoneHint}</p> : null}
          {ui.statusDetail ? <p className="text-muted">{ui.statusDetail}</p> : null}

          <div className="row">
            {voice.audioPlaybackBlocked ? (
              <button type="button" className="button button-primary" onClick={() => void voice.enableAudio()}>
                {strings.voice.enableHuddleAudio}
              </button>
            ) : null}
            {ui.showEnableMicButton ? (
              <button type="button" className="button button-primary" onClick={() => void voice.toggleMuted()}>
                {strings.voice.joinMicrophone}
              </button>
            ) : null}
            {ui.showMuteButton ? (
              <button
                type="button"
                className="button"
                aria-pressed={ui.muteButtonPressed}
                onClick={() => void voice.toggleMuted()}
              >
                {ui.muteButtonLabel}
              </button>
            ) : null}
            {ui.showRetry ? (
              <button type="button" className="button" onClick={voice.retry}>
                {strings.voice.retry}
              </button>
            ) : null}
            {ui.showListeningOnlyNote ? <span className="badge">{strings.voice.listeningOnly}</span> : null}
          </div>

          <ParticipantStrip
            title={stripTitle}
            participants={session.participants}
            speaking={voice.speaking}
            reducedMotion={reducedMotion}
            compact={compact}
          />
        </>
      )}
    </section>
  );
}

function ParticipantStrip({
  title,
  participants,
  speaking,
  reducedMotion,
  compact,
}: {
  title: string;
  participants: ParticipantPublic[];
  speaking: ReadonlySet<string>;
  reducedMotion: boolean;
  compact?: boolean;
}) {
  return (
    <div className="stack-sm">
      {compact ? null : <h3>{title}</h3>}
      <ul
        className="row"
        role="list"
        aria-label={title}
        style={{ listStyle: 'none', margin: 0, padding: 0, overflowX: 'auto', flexWrap: compact ? 'nowrap' : 'wrap' }}
      >
        {participants.map((participant) => {
          const isSpeaking = speaking.has(participant.id);
          return (
            <li key={participant.id} className="stack-sm" style={{ alignItems: 'center', minWidth: compact ? 72 : 96 }}>
              <span
                className={isSpeaking && !reducedMotion ? 'voice-speaking-pulse' : undefined}
                style={{ display: 'inline-flex', borderRadius: '999px' }}
              >
                <CharacterBadge slot={participant.characterSlot} displayName={participant.displayName} size={compact ? 28 : 40} />
              </span>
              <span className="row" role="list" aria-label="Voice status" style={{ gap: 'var(--space-1)' }}>
                {isSpeaking ? <span className="badge badge-accent">{strings.voice.speaking}</span> : null}
                {participant.muted ? <span className="badge">{strings.voice.muted}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Persistent controls share the session's existing connection; this component never opens a mic itself. */
function CompactVoiceStrip({ voice }: { voice: UseVoiceResult }) {
  const { view, self } = useSessionContext();
  const decisionNotes = useDecisionNoteBridge()?.controls ?? null;
  const ui = voiceUiState(voice);
  const projection = !view.child && view.root?.status === 'active' ? BoardView.safeParse(view.root.view) : null;
  const focus = view.child ?? view.root;
  const decision = focus?.moduleId === DECISION_MAKER_ID ? DecisionMakerView.safeParse(focus.view) : null;
  const potato = focus?.moduleId === HOT_POTATO_ID ? HotPotatoView.safeParse(focus.view) : null;
  const turnIds = new Set(projection?.success ? projection.data.tokens.filter((token) => token.isTurnHolder).map((token) => token.participantId) : []);
  if (decision?.success && decision.data.deciderId) turnIds.add(decision.data.deciderId);
  const mock = voice.provider === 'mock' || voice.simulated;
  if (potato?.success && potato.data.phase === 'speaking' && potato.data.holderId) turnIds.add(potato.data.holderId);
  const berryAvatars = view.session.status === 'lobby' || MOMENTUM_WORLD_MODULE_IDS.includes(view.root?.moduleId ?? '') || focus?.moduleId === DECISION_MAKER_ID || focus?.moduleId === HOT_POTATO_ID;
  const participants = view.session.participants.filter((participant) => participant.role === 'player' && participant.connection !== 'removed' && participant.connection !== 'left');
  const displayedParticipants = decisionNotes
    ? decisionNotes.participants.flatMap((note) => {
        const participant = participants.find((candidate) => candidate.id === note.id);
        return participant ? [participant] : [];
      })
    : potato?.success ? potato.data.participantIds.flatMap(id => participants.filter(p => p.id === id)) : participants;
  const statusLabel = ui.isOff
    ? 'Voice off'
    : mock
      ? 'Huddle not connected · demo controls only'
      : voice.mode === 'in_person'
        ? 'In person'
        : voice.status === 'connected' && voice.muted
          ? strings.voice.statusListening
          : ui.statusLabel;
  return (
    <section className="momentum-voice-strip" aria-label="Voice controls">
      <div className="momentum-voice-strip__main">
        <ul className="momentum-voice-strip__people" aria-label="Players and voice status">
          {decisionNotes ? (
            <li className="momentum-voice-strip__player momentum-voice-strip__client" title="Customer note · game character">
              <button type="button" className="momentum-voice-strip__note" aria-label="Customer note, game character" aria-pressed={decisionNotes.activeNoteId === 'customer'} onClick={() => decisionNotes.openNote('customer')}>
                <ReceiverIcon />
              </button>
              <span className="momentum-voice-strip__name" aria-hidden="true">Customer</span>
            </li>
          ) : null}
          {displayedParticipants.map((participant) => {
            const disconnected = participant.connection !== 'connected';
            const muted = participant.simulated || (participant.id === self?.participantId ? voice.muted : participant.muted);
            const speaking = !ui.isOff && !mock && voice.status === 'connected' && !disconnected && !muted && voice.speaking.has(participant.id);
            const turn = turnIds.has(participant.id);
            const state = participant.simulated ? 'Demo teammate, no microphone' : disconnected ? 'Disconnected from session' : ui.isOff ? 'Voice off' : mock ? 'Mock voice, no audio' : voice.status !== 'connected' ? ui.statusLabel : muted ? 'Microphone muted' : speaking ? 'Speaking' : 'Microphone on';
            const label = `${participant.displayName}: ${state}${turn ? ', current turn' : ''}`;
            const note = decisionNotes?.participants.find((candidate) => candidate.id === participant.id) ?? null;
            const noteLabel = note
              ? `${participant.displayName}, ${note.own ? 'your private note' : note.private ? 'private note' : 'note'}, ${note.submitted ? 'suggestion submitted' : 'no suggestion submitted'}. ${label}`
              : label;
            const portrait = (
              <span className="momentum-voice-strip__avatar">
                {berryAvatars ? <BerryAvatar slot={participant.characterSlot} className="momentum-voice-strip__badge momentum-voice-strip__berry" /> : <CharacterBadge className="momentum-voice-strip__badge" slot={participant.characterSlot} displayName={participant.displayName} size={30} />}
                {turn ? <span className="momentum-voice-strip__turn" aria-hidden="true">◆</span> : null}
                <span className="momentum-voice-strip__state" aria-hidden="true">{disconnected ? '×' : muted || ui.isOff ? <MicIcon muted /> : null}</span>
              </span>
            );
            return (
              <li key={participant.id} className={`momentum-voice-strip__player${speaking ? ' is-speaking' : ''}${disconnected ? ' is-disconnected' : ''}`} aria-label={note ? undefined : label} title={noteLabel}>
                {note ? (
                  <button type="button" className="momentum-voice-strip__note" aria-label={noteLabel} aria-pressed={decisionNotes?.activeNoteId === participant.id} onClick={() => decisionNotes?.openNote(participant.id)}>
                    {portrait}
                  </button>
                ) : portrait}
                <span className="momentum-voice-strip__name" aria-hidden="true">{participant.displayName}</span>
              </li>
            );
          })}
        </ul>
        {!ui.isOff && (ui.showEnableMicButton || ui.showMuteButton) ? (
          <button type="button" className="momentum-voice-strip__mic" aria-label={mock ? `Mock control: ${ui.muteButtonLabel}` : ui.showEnableMicButton ? strings.voice.joinMicrophone : ui.muteButtonLabel} aria-pressed={!voice.muted} onClick={() => void voice.toggleMuted()}>
            <MicIcon muted={voice.muted} />
            <span>{voice.muted ? 'Mic off' : 'Mic on'}</span>
          </button>
        ) : null}
        {voice.audioPlaybackBlocked ? <button type="button" className="momentum-voice-strip__mic" onClick={() => void voice.enableAudio()}>{strings.voice.enableHuddleAudio}</button> : null}
        {ui.showRetry ? <button type="button" className="momentum-voice-strip__mic" onClick={voice.retry}>{strings.voice.retry}</button> : null}
      </div>
      <p className="momentum-voice-strip__status" role="status" aria-live="polite">{statusLabel}{ui.showListeningOnlyNote ? ' · Listening only' : ''}{turnIds.size > 0 ? ' · ◆ Turn' : ''}</p>
      {ui.statusDetail ? <p className="momentum-voice-strip__detail">{ui.statusDetail}</p> : null}
    </section>
  );
}

function MicIcon({ muted = false }: { muted?: boolean }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" />{muted ? <path d="M3 3l18 18" /> : null}</svg>;
}

function ReceiverIcon() {
  return (
    <span className="momentum-voice-strip__receiver" aria-hidden="true">
      <svg viewBox="0 0 36 36" width="34" height="34">
        <rect x="3" y="3" width="30" height="30" rx="11" fill="#171a20" />
        <rect x="10" y="13" width="5" height="5" rx="1" fill="white" />
        <rect x="21" y="13" width="5" height="5" rx="1" fill="white" />
      </svg>
    </span>
  );
}

/** Decorative portrait: the containing player item provides the name and all live state labels. */
